<template>
  <b-modal
    id="force-change-password-modal"
    :visible="isVisible"
    :title="translations[308]"
    centered
    dialog-class="modal-dialog-md-center"
    body-class="bg-primary-100"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
  >
    <p class="fs-6 lh-sm mb-3">
      Willkommen! Bitte legen Sie ein eigenes Passwort fest, um Ihr Konto zu aktivieren.
    </p>
    <b-form class="force-password-form" autocomplete="new-password" @submit.prevent="onSubmit">
      <b-alert :show="!!errorText" variant="danger">
        {{ errorText }}
      </b-alert>
      <b-input-group class="mb-1">
        <PasswordPattern size="lg"
                         class-widget="border-0"
                         id="force-password-new"
                         v-model.trim="passwordNew"
                         :field-type="passwordFieldType"
                         :placeholder="translations[311]"
                         :title-widget="translations[2521]"/>
        <b-input-group-append>
          <b-button variant="custom-light" :aria-label="translations[311]" @click="isPasswordVisible = !isPasswordVisible">
            <b-icon :icon="passwordFieldIcon"></b-icon>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-input-group class="mb-1">
        <PasswordPattern size="lg"
                         class-widget="border-0"
                         id="force-password-new-confirm"
                         v-model.trim="passwordNewConfirm"
                         :field-type="passwordConfirmFieldType"
                         :placeholder="translations[312]"
                         :title-widget="translations[2521]"/>
        <b-input-group-append>
          <b-button variant="custom-light" :aria-label="translations[312]" @click="isPasswordConfirmVisible = !isPasswordConfirmVisible">
            <b-icon :icon="passwordConfirmFieldIcon"></b-icon>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <!-- static checklist instead of PasswordPattern's popover, which would cover the footer button -->
      <div class="mt-3 fs-6">
        <p class="mb-2">{{ translations[2524] }}:</p>
        <ul class="list-unstyled m-0">
          <li
            v-for="rule in passwordRules"
            :key="rule.id"
            class="d-flex align-items-start mb-1"
            :class="rule.met ? 'text-success' : 'text-muted'"
          >
            <b-icon aria-hidden="true" :icon="rule.met ? 'check-lg' : 'dot'" class="mr-2 flex-shrink-0" scale="1.1" />
            <span v-html="translations[rule.id]"></span>
          </li>
        </ul>
      </div>
      <!-- hidden submit so Enter in a field submits the form -->
      <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
    </b-form>
    <template #modal-footer>
      <b-row class="gx-2 w-100" align-v="center">
        <b-col cols="auto">
          <b-button variant="primary" :disabled="isSubmitting" @click="onSubmit">
            <b-spinner v-if="isSubmitting" small class="mr-1" />
            {{ translations[313] }}
          </b-button>
        </b-col>
        <b-col cols="auto" class="ml-auto">
          <button type="button" class="btn-inline-link fs-6 text-aba-primary-800" @click="onLogout">{{ translations[13575] }}</button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { changePasswordApplicantForced } from '@/api/Auth';
import PasswordPattern from '@/components/Widget/PasswordPattern.vue';

const PASSWORD_PATTERN = /(?=^.{8,}$)(?=.*[0-9])(?=.*[A-Z]).*$/;

@Component({
  components: { PasswordPattern },
})
export default class ForceChangePasswordModal extends Vue {
  passwordNew = '';

  passwordNewConfirm = '';

  isPasswordVisible = false;

  isPasswordConfirmVisible = false;

  isSubmitting = false;

  errorText = '';

  get translations() {
    return AppModule.translation;
  }

  // The single_use_token login in the router fetches /user/info before App.vue has loaded
  // translations — wait for the initial data, otherwise the dialog opens with empty labels.
  get isVisible() {
    return UserModule.isForceChangePassword && AppModule.initialDataLoaded;
  }

  get passwordRules() {
    return [
      { id: 2527, met: /[A-Z]/.test(this.passwordNew) },
      { id: 2528, met: /\d/.test(this.passwordNew) },
      { id: 2529, met: this.passwordNew.length >= 8 },
    ];
  }

  get passwordFieldType() {
    return this.isPasswordVisible ? 'text' : 'password';
  }

  get passwordFieldIcon() {
    return this.isPasswordVisible ? 'eye-slash' : 'eye';
  }

  get passwordConfirmFieldType() {
    return this.isPasswordConfirmVisible ? 'text' : 'password';
  }

  get passwordConfirmFieldIcon() {
    return this.isPasswordConfirmVisible ? 'eye-slash' : 'eye';
  }

  @Watch('passwordNew')
  @Watch('passwordNewConfirm')
  onPasswordInput() {
    this.errorText = '';
  }

  async onSubmit() {
    if (this.isSubmitting) return;

    const validationError = this.getValidationError();
    if (validationError) {
      this.errorText = validationError;
      return;
    }

    this.isSubmitting = true;
    try {
      await changePasswordApplicantForced({
        password_new: this.passwordNew,
        password_new_confirm: this.passwordNewConfirm,
      });
      UserModule.SET_FORCE_CHANGE_PASSWORD_DONE();
      this.passwordNew = '';
      this.passwordNewConfirm = '';
      this.$bvToast.toast(this.translations[318], {
        variant: 'success',
        solid: true,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      });
    } catch (error: any) {
      this.errorText = this.getErrorText(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  onLogout() {
    UserModule.LogOutApplicant(AppModule.sid);
    this.$router.push({ path: '/', query: { lang: AppModule.language } }).catch(() => ({}));
  }

  // same rules the backend enforces — checked here so the user sees the reason right away
  getValidationError(): string {
    if (!PASSWORD_PATTERN.test(this.passwordNew)) {
      return this.translations[2524];
    }
    if (this.passwordNew !== this.passwordNewConfirm) {
      return this.translations[240];
    }
    return '';
  }

  // backend messages of POST /user/change_password
  getErrorText(error: any): string {
    const message = error?.response?.data?.message;
    switch (message) {
      // 403: new password equals the generated one
      case 'old and new passwords must be different':
        return this.translations[12149];
      // 403: confirmation differs
      case 'passwords does not match':
        return this.translations[240];
      // 403: less than 8 chars / no digit / no capital letter
      case 'wrong password pattern':
        return this.translations[2524];
      // 400: the force flag is gone (e.g. /user/info was called again), old_password would be required now
      case 'invalid params':
        return 'Der Link ist nicht mehr gültig. Bitte fordern Sie über „Passwort vergessen“ einen neuen Link an.';
      default:
        return 'Das Passwort konnte nicht geändert werden. Bitte versuchen Sie es erneut.';
    }
  }
}
</script>

<style lang="scss" scoped>
.force-password-form ::v-deep .password-widget__content {
  display: none;
}
</style>
